<template>
    <div v-if="asset">
        <h2 class="create-asset__heading heading heading--lg">
            {{ getSectionHeader }}
        </h2>

        <div class="idivider divider--20"></div>

        <ReadOnlyInputItem
            rounded
            name="name"
            :value="asset.name"
            label="Name"
            description="loaded from YCA file"
            @change="onassetChanged"
            @input="updateValue"
        />

        <ReadOnlyInputItem
            tag="textarea"
            name="description"
            description="loaded from YCA file"
            :value="asset.description"
            label="Description"
            validate="max:250"
            @change="onassetChanged"
            @input="updateValue"
        />

        <label v-if="isBlueprintAsset" class="label">Tags</label>
        <label v-else class="label">Genre</label>
        <div class="create-asset__input create-asset__tag-selector clearfix">
            <TagPicker
                class="find-tag find-tag--short"
                :selected-tags="selectedTags"
                :tag-options="tagOptions"
                :max-tags="1"
                :searchable="true"
                :is-blueprint="isBlueprintAsset"
                :allow-create="false"
                :header="false"
                @search="searchTagsOnChange"
                @remove="onTagsRemove"
                @change="onTagsChanged"
                @scroll="searchTagsOnScroll"
                @add="addTagToSelected"
            />
        </div>

        <InputItem
            name="changelog"
            :value="asset.changelog"
            label="Changelog"
            placeholder="Latest changelog"
            validate="max:125"
            @change="onassetChanged"
            @input="updateValue"
        />

        <div v-if="asset.content_type != 2">
            <div v-if="!asset.single_player_only || !spOnly" class="row">
                <input
                    id="asset-is-protected-checkbox"
                    v-model="isProtected"
                    :disabled="
                        (asset.monetized_actions &&
                            asset.monetized_actions.length > 0) ||
                            asset.protected ||
                            disableProtect
                    "
                    type="checkbox"
                    :class="{ active: hover }"
                    @change="onMonetizationChanged"
                />
                <div class="protected-label">
                    <label for="asset-is-protected-checkbox"
                        >Disable local hosting (P2P)</label
                    >
                </div>
            </div>

            <article
                v-if="!asset.single_player_only || !spOnly"
                class="message"
                :class="accordionClasses"
            >
                <div class="message-header" @click="toggleAccordion">
                    <div class="message-header-title">
                        With this checked, players will not be able to host this
                        game in a multiplayer game on their PCs.
                    </div>
                    <Icon
                        :type="!isOpen ? 'plus' : 'minus'"
                        color="#fea201"
                        size="1"
                        padding="0"
                    />
                </div>
                <div class="message-body">
                    <div class="message-content">
                        With this checked, players will not be able to host this
                        game in a multiplayer game on their PCs, but on
                        dedicated servers only (either ones spawned
                        automatically by us or rented by players), which will
                        make your game source file safer. Local hosting is not
                        possible if your game supports monetization or global
                        user data. Once you disable local hosting, it can't be
                        enabled again.
                    </div>
                </div>
            </article>
            <div v-if="!asset.single_player_only || !spOnly">
                <div
                    v-if="
                        asset.monetized_actions &&
                            asset.monetized_actions.length > 0 &&
                            disableProtect
                    "
                    class="not-supported"
                >
                    Local hosting is not supported (your game contains
                    monetization elements and uses global user data).
                </div>
                <div
                    v-if="
                        asset.monetized_actions &&
                            asset.monetized_actions.length > 0 &&
                            !disableProtect
                    "
                    class="not-supported"
                >
                    Local hosting is not supported (your game contains
                    monetization elements).
                </div>
                <div
                    v-if="
                        (disableProtect &&
                            asset.monetized_actions &&
                            asset.monetized_actions.length === 0) ||
                            (disableProtect && !asset.monetized_actions)
                    "
                    class="not-supported"
                >
                    Local hosting is not supported (your game uses global user
                    data)
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import TagPicker from '@/components/common/TagPicker.vue';
import InputItem from './InputItem.vue';
import ReadOnlyInputItem from './read-only-input-item';
import Icon from '@/components/partials/IconRounded';
import { mapActions, mapGetters } from 'vuex';
import TagsResource from '@/services/tags';
import isEqual from 'lodash.isequal';

import { addErrorNotification } from '../../lib/notifications';


const defaultBlueprintTags = [
    {name: 'YlandsBluecomp_Building', value: 'building' },
    {name: 'YlandsBluecomp_Ship', value: 'ship' },
    {name: 'YlandsBluecomp_Car', value: 'car' },
    {name: 'YlandsBluecomp_Decoration', value: 'decoration' },
    {name: 'YlandsBluecomp_medieval', value: 'cave' },
    {name: 'YlandsBluecomp_Native', value: 'native' },
    {name: 'YlandsBluecomp_WildWest', value: 'wild west' },
    {name: 'YlandsBluecomp_YlandiumPunk', value: 'ylandium punk' },
    {name: 'YlandsBluecomp_Elven', value: 'elven' },
    {name: 'YlandsBluecomp_Nature', value: 'nature' },
    {name: 'YlandsBluecomp_Modern', value: 'modern' },
    {name: 'YlandsBluecomp_Ancient', value: 'ancient' },
    {name: 'YlandsBluecomp_Alien', value: 'alien' },
    {name: 'YlandsBluecomp_Script', value: 'script' },
]

export default {
    components: {
        TagPicker,
        InputItem,
        ReadOnlyInputItem,
        Icon,
    },
    props: {
        asset: {
            type: Object,
            default: () => {},
        },
        isNew: {
            type: Boolean,
            default: true,
        },
        listeners: {
            type: Object,
            default: () => {},
        },
        
    },
    data() {
        return {
            disableProtect: false,
            isProtected: true,
            hover: false,
            isOpen: false,
            spOnly: false,
            blueprintTypeIds: [2,4],
            tagOptions: [],
            newTags: [],
            selectedTags: [],
            maxTags: 1,
        };
    },
    computed: {
        isBlueprintAsset() {
            return this.blueprintTypeIds.includes(this.asset.content_type);
        },
        accordionClasses: function() {
            return {
                'is-closed': !this.isOpen,
                'is-primary': this.isOpen,
                'is-dark': !this.isOpen,
            };
        },
        // selectedTags() {
        //    return  this.asset?.tags || [];
        // },
        getSectionHeader() {
            return this.isNew ? 'New Creation' : 'Edit Creation';
        },
    },
    mounted() {
        if (this.asset) {
            this.asset.metadata.features.map(feature => {
                if (
                    feature === 'HasLeaderboards' ||
                    feature === 'HasUserData'
                ) {
                    this.disableProtect = true;
                } else if (feature === 'SpOnly') {
                    this.spOnly = true;
                }
            });
            this.isProtected =
                this.asset.protected ||
                (this.asset.monetized_actions &&
                    this.asset.monetized_actions.length > 0) ||
                this.disableProtect;

            //filthy hack to set protected dynamically on component render
            this.$emit('change', {
                isProtected: this.isProtected,
                valid: true,
            });

            if (this.asset.single_player_only || this.asset.content_type == 2) {
                this.isProtected = false;
                this.spOnly = true;
            }

            this.selectedTags = this.asset?.tags || [];
        
            if (this.isBlueprintAsset)  {
                this.maxTags = 5;
                this.tagOptions = defaultBlueprintTags;
            }
        }
    },
    watch: {
        tagOptions(tags, oldTags) {
            if (!isEqual(tags, oldTags) && !this.isBlueprintAsset) {
                this.refetchTagsByIds(tags);
            }
        },
        selectedTags(newSelected, oldTags) {
            if (this.isBlueprintAsset) {
                const tagOptions = [...defaultBlueprintTags];
                this.tagOptions = [...tagOptions.filter(tag => 
                    !newSelected.some(selected => selected.name === tag.name)
                )]
            }

        },
    },
    methods: {
        ...mapActions('tags', {
            fetchTags: 'getTags',
        }),
        refetchTagsByIds(ids) {
            if (ids && ids.length > 0) {
                if (typeof ids[0] === 'object') {
                    this.activeTags = ids;
                } else {
                    TagsResource.getTags({
                        ids: ids,
                    }).then(response => {
                        this.selectedTags = response.data.rows.concat(
                            this.newTags
                        );
                    });
                }
            }
        },
         remove(tags) {
            this.$emit('change', tags);
        },
        async searchTagsOnChange(name) {
            const res =await this.fetchTags({
                name: `YlandsGenre_${name}`,
                limit: 20,
                offset: 0,
            }).catch(err => console.log(err))
        
            this.tagOptions = res.rows;
            this.tagsCount = res.count;
        },
        searchTagsOnScroll(name, page) {
            this.fetchTags({
                name: `YlandsGenre_${name}`,
                limit: 20,
                offset: page < this.totalPages ? page : this.totalPages,
            }).then(res => {
                this.tagOptions.push(...res.rows);
            });
        },
        addTagToSelected(tag) {
            if (tag && this.selectedTags.length < this.maxTags) {

                const tagExists = this.selectedTags.find(
                    selectedTag => selectedTag.name === tag.name
                );
                if (tagExists) return;

                this.selectedTags.push(tag);
                this.tagOptions = this.tagOptions.filter(
                    option => option.name !== tag.name
                );

                // if (!tag.id) {
                //     this.newTags = [...this.newTags, tag];
                // }

                this.asset.tags = this.selectedTags;
                this.onassetChanged();
            } else {
                addErrorNotification(
                    `You can only select ${this.maxTags} ${this.isBlueprintAsset ? 'tag' : 'genre'} ${
                        this.maxTags > 1 ? 's' : ''
                    }`
                );
            }
        },
       
        toggleAccordion: function() {
            this.isOpen = !this.isOpen;
        },
        async onMonetizationChanged() {
            let valid = true;
            try {
                await this.$validator.validateAll();
            } catch (e) {
                valid = false;
            }
            this.$emit('change', {
                isProtected: this.isProtected,
                valid,
            });
        },
        onTagsRemove(selectedTags) {
            console.log(selectedTags)
        },
        onTagsChanged(tags) {
            this.asset.tags = tags;
            this.onassetChanged();
        },
        onassetChanged() {
            this.$validator
                .validateAll()
                .then(success => {
                    this.listeners.change({
                        tags: this.asset.tags,
                        protected: this.asset.protected,
                        changelog: this.asset.changelog,
                        valid: success,
                    });
                })
                .catch(() => {
                    this.listeners.change({ asset: this.asset, valid: false });
                });
        },
        updateValue(name, val) {
            if (name == 'changelog' && !val) {
                this.asset['changelog'] = '';
            } else {
                this.asset[name] = val;
            }
        },
    },
};
</script>
<style scoped lang="scss">
.not-supported {
    padding-top: 20px;
    color: #f8451d;
    font-size: 15px;
}

.message {
    margin-left: auto;
    margin-right: auto;
}

.message-header {
    cursor: pointer;
    padding-top: 8px;
    display: flex;

    .message-header-title {
        align-self: center;
        margin-right: 10px;
    }
}

.message-body {
    padding: 0;
    max-height: 10em;
    overflow: hidden;
    transition: 0.3s ease all;
}

.is-closed .message-body {
    max-height: 0;
}

.message-content {
    padding-top: 20px;
}
.label {
    display: block;
    font-size: 1.14286rem;
    color: #223449;
    font-weight: 600;
    margin-bottom: 0.5em;
}
.row {
    display: flex;
    align-items: center;

    .protected-label {
        margin-left: 1em;
    }
}
</style>
